import { cn } from 'cn';

import { type WatermarkType } from '~/data/invoice-reprocessing';

import { Icon } from '@vestigas/ui/atoms';

type P = {
  readonly watermarkType: WatermarkType;
} & ComponentStyling;

const Fail = () => (
  <Icon className="size-5 text-tiga-semantic-error" metaphor="failed" />
);

const Success = () => (
  <Icon className="size-5 text-tiga-surface-default" metaphor="check" />
);

const Warning = () => (
  <Icon
    className="size-5 scale-80 text-tiga-semantic-danger"
    metaphor="warningSlim"
  />
);

const NotApplicable = () => (
  <Icon
    className="size-5 text-tiga-surface-default"
    metaphor="horizontalLine"
  />
);

export const InvoiceCheckWatermark = ({
  className,
  watermarkType,
  style,
}: P) => {
  if (!watermarkType) {
    return null;
  }

  let watermark = null;

  if (watermarkType.startsWith('erroneous-')) {
    const [
      formalCheck,
      deliveryCheck,
      signatureCheck,
      amountCheck,
      priceCheck,
    ] = [...watermarkType.split('-')[1]];
    const checks = [
      formalCheck,
      deliveryCheck,
      signatureCheck,
      amountCheck,
      priceCheck,
    ].filter(Boolean);

    const hasErrors = checks.includes('e');
    const isFlawless = checks.every((check) => check === 's');
    const hasNeutralChecks = !hasErrors && !isFlawless;

    watermark = (
      <div
        className={cn(
          'flex h-6 items-center justify-center -space-x-1 border-y px-0.5',
          {
            'border-tiga-semantic-error bg-tiga-semantic-error/5': hasErrors,
            'border-tiga-surface-default bg-white/80':
              isFlawless || hasNeutralChecks,
          },
        )}
      >
        {checks.map((check, index) => {
          const getCheckComponent = (checkValue: string) => {
            switch (checkValue) {
              case 's': {
                return <Success key={`${checkValue}-${index}`} />;
              }

              case 'w': {
                return <Warning key={`${checkValue}-${index}`} />;
              }

              case 'n': {
                return <NotApplicable key={`${checkValue}-${index}`} />;
              }

              default: {
                return <Fail key={`${checkValue}-${index}`} />;
              }
            }
          };

          return getCheckComponent(check);
        })}
      </div>
    );
  }

  switch (watermarkType) {
    case 'correct': {
      watermark = (
        <div className="flex h-6 items-center justify-center -space-x-1 border-y border-tiga-surface-default bg-white/80 px-0.5">
          <Success />
        </div>
      );
      break;
    }

    case 'erroneous': {
      watermark = (
        <div className="flex h-6 items-center justify-center -space-x-1 border-y border-tiga-semantic-error bg-tiga-semantic-error/5 px-0.5">
          <Fail />
        </div>
      );
      break;
    }

    case 'grey-credit_note': {
      watermark = <div className="text-tiga-surface-default">credit note</div>;
      break;
    }

    case 'grey-dln_missing': {
      watermark = <div className="text-tiga-surface-default">no dln</div>;
      break;
    }

    case 'grey-dln_reference_missing': {
      watermark = <div className="text-tiga-surface-default">no dln ref</div>;
      break;
    }
  }

  // The backend can introduce watermarks this app has no icon for.
  watermark ??= (
    <div className="text-tiga-surface-default">{watermarkType}</div>
  );

  return (
    <div className={className} style={style}>
      {watermark}
    </div>
  );
};
